import { useMutation, useQuery } from "@tanstack/react-query";
import { ClientOnly, Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  Clock,
  Link2,
  Loader2,
  LucideBrain,
  ShieldCheck,
  Store,
  Trophy,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { ContactUs } from "@/features/shared/components/contact-us";
import { MapRotation } from "@/features/game/components/map-rotation";
import { PredatorThreshold } from "@/features/game/components/predator-threshold";
import { ServerStatus } from "@/features/game/components/server-status";
import { DefaultImageCard } from "@/features/home/components/image-card";
import { useWechatShare } from "@/hooks/use-wechat-share";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { UpCard } from "@/features/home/components/up-card";
import { WechatReviewSubscriptionCard } from "@/features/shared/components/wechat-review-subscription-card";
import { latestNewsHighlightQueryOptions } from "@/lib/client/apex-news";
import {
  getWechatAuthUrl,
  getWechatBindStatus,
  WECHAT_BIND_STATUS_QUERY_KEY,
} from "@/lib/client/wechat-auth";
import { formatNewsUtcDate } from "@/lib/utils/date";
import { isWeChatBrowser } from "@/lib/utils/wechat";
import { getNewsDisplayLabel, toNewsImageUrlForRender } from "@/lib/utils/apex-news";
import type { CurrentUser } from "@/lib/client/user";

const HOME_SHARE_TITLE = "CN Apex Club - 专业 Apex Legends 游戏社区 | 复盘分析 | 技巧提升";
const HOME_SHARE_DESCRIPTION =
  "加入 CN Apex Club，享受专业的 Apex Legends 复盘服务，AI 智能分析，技巧测评和战术指导。提升你的游戏水平，成为更强的传奇玩家！";
const TAOBAO_AFFILIATE_SHOP_URL = "https://s.click.taobao.com/HfQboKl";
const TAOBAO_BATTLE_PASS_SHOP_URL = "https://s.click.taobao.com/R3UNcui";
const TAOBAO_AFFILIATE_IMAGE_SUFFIX = "_100x100.jpg_.webp";
const TAOBAO_AFFILIATE_PRODUCTS = [
  {
    name: "小水怪挂件",
    image:
      `https://img.alicdn.com/bao/uploaded/O1CN01lT9FAv1zp3JgIbSBx_!!6000000006762-0-yinhe.jpg${TAOBAO_AFFILIATE_IMAGE_SUFFIX}`,
  },
  {
    name: "Nessie 抱枕",
    image:
      `https://img.alicdn.com/bao/uploaded/O1CN01jIwpvV27XCd3skuxL_!!6000000007806-0-yinhe.jpg${TAOBAO_AFFILIATE_IMAGE_SUFFIX}`,
  },
  {
    name: "猎杀钥匙扣",
    image:
      `https://img.alicdn.com/bao/uploaded/O1CN01O2iHPj1xHV2fyeM2Z_!!6000000006418-0-yinhe.jpg${TAOBAO_AFFILIATE_IMAGE_SUFFIX}`,
  },
] as const;

export function HomePage({ currentUser }: { currentUser: CurrentUser | null }) {
  useWechatShare({
    enabled: true,
    title: HOME_SHARE_TITLE,
    desc: HOME_SHARE_DESCRIPTION,
    imgUrl: "/nessies.png",
  });

  return (
    <div className="size-full grid gap-4">
      <HomeWechatBindBanner currentUser={currentUser} />
      <WechatReviewSubscriptionCard currentUser={currentUser} context="home" />

      <DefaultImageCard image="/images/home/review.webp" priority>
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white text-end drop-shadow-md text-shadow-md">
          高手复盘
        </h1>
        <p className="text-white/90 text-end text-lg md:text-xl font-medium drop-shadow mt-2 text-shadow-md">
          复盘是提高游戏水平的重要途径
        </p>

        <div className="flex mt-4!">
          <Link data-umami-event="enter-history" to="/history">
            <Button variant="link" className="text-blue-500 text-shadow-md">
              历史记录
            </Button>
          </Link>
          <Link data-umami-event="enter-application" to="/application">
            <Button>申请复盘</Button>
          </Link>
        </div>
      </DefaultImageCard>

      <HomeNewsHighlightBlock />

      <TaobaoBattlePassShopCard />

      <DefaultImageCard image="/images/home/circle.webp">
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white text-end drop-shadow-md text-shadow-md">
          猜圈游戏
        </h1>
        <p className="text-white/90 text-end text-lg md:text-xl font-medium drop-shadow mt-2 text-shadow-md">
          Who&apos;s the best IGL?
        </p>

        <div className="flex mt-4!">
          <Link data-umami-event="enter-ring-rank" to="/game/ring-rank">
            <Button variant="link" className="text-yellow-400 text-shadow-md">
              <Trophy className="size-4 -mr-1" />
              排行榜
            </Button>
          </Link>
          <Link data-umami-event="enter-ring-game" to="/game/ring">
            <Button>开始游戏</Button>
          </Link>
        </div>
      </DefaultImageCard>

      <TaobaoAffiliateShopCard />

      <Card className="group hover:shadow-lg transition-all duration-200 border-border/50 bg-card/50 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-500/10 rounded-lg ring-1 ring-red-500/20">
              <LucideBrain className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold tracking-tight">Apex 打法风格锐评</CardTitle>
              <CardDescription className="text-muted-foreground text-xs">
                通过10个问题分析你的游戏风格，AI会锐评你的风格并提供建议
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground/80 mb-4 leading-relaxed">
            选择你的位置，回答关于游戏策略、心理素质和团队配合的问题。AI答复仅供参考，AI知识库可能落后多个版本。
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-xs text-muted-foreground font-medium">
              <Clock className="w-4 h-4" />
              <span className="font-mono">约5-8分钟</span>
            </div>
            <Link data-umami-event="enter-apex-style" to="/game/apex-style">
              <Button className="font-semibold shadow-sm">开始游戏</Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <ClientOnly>
        <MapRotation />
      </ClientOnly>

      <ClientOnly>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PredatorThreshold />
          <ServerStatus />
        </div>
      </ClientOnly>

      <ClientOnly>
        <UpCard />
      </ClientOnly>

      <ContactUs />
    </div>
  );
}

function TaobaoAffiliateShopCard() {
  return (
    <Card className="relative overflow-hidden border-cyan-500/25 bg-[linear-gradient(135deg,rgba(2,6,23,0.96),rgba(15,23,42,0.93)_58%,rgba(24,24,27,0.96))] p-0 text-white shadow-md shadow-cyan-950/10">
      <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-cyan-200/70 to-transparent" />
      <CardContent className="relative p-3 sm:p-4">
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-cyan-200/35 bg-cyan-300/12 px-2.5 py-1 text-xs font-semibold text-cyan-50">
              <Store className="size-3.5 shrink-0" />
              <span className="truncate">十一老师要大电</span>
            </div>
          </div>

          <div className="flex shrink-0 gap-1.5 overflow-hidden sm:gap-2">
            {TAOBAO_AFFILIATE_PRODUCTS.map((product) => (
              <img
                key={product.name}
                src={product.image}
                alt={product.name}
                className="size-9 rounded-md border border-white/15 bg-white object-contain shadow-sm sm:size-12"
                width={48}
                height={48}
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
              />
            ))}
          </div>

          <a
            data-umami-event="home-taobao-affiliate-shop"
            href={TAOBAO_AFFILIATE_SHOP_URL}
            target="_blank"
            rel="noreferrer sponsored"
            className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-md bg-cyan-300 px-3 text-xs font-bold text-slate-950 shadow-sm shadow-cyan-950/25 transition hover:bg-cyan-200 active:scale-[0.98] sm:h-9 sm:gap-2 sm:text-sm"
          >
            去逛
            <ArrowUpRight className="size-3.5 sm:size-4" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

function TaobaoBattlePassShopCard() {
  return (
    <Card className="overflow-hidden border-orange-500/25 bg-[linear-gradient(135deg,rgba(24,24,27,0.96),rgba(67,20,7,0.92))] p-0 text-white shadow-sm">
      <CardContent className="flex items-center gap-3 p-3 sm:p-4">
        <img
          src="/images/home/taobao-battle-pass-shop.webp"
          alt="蟹宝电玩店铺头像"
          className="size-10 shrink-0 rounded-lg border border-orange-300/20 bg-white object-contain"
          width={40}
          height={40}
          loading="lazy"
          decoding="async"
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold tracking-tight sm:text-base">通行证 · 赠礼</h2>
          <p className="mt-0.5 text-xs text-orange-100/70">淘宝合作店铺</p>
        </div>
        <a
          data-umami-event="home-taobao-battle-pass-shop"
          href={TAOBAO_BATTLE_PASS_SHOP_URL}
          target="_blank"
          rel="noopener noreferrer sponsored"
          aria-label="前往淘宝合作店铺，查看通行证、赠礼等商品（新窗口打开）"
          className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-md bg-orange-300 px-3 text-xs font-bold text-orange-950 shadow-sm transition hover:bg-orange-200 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-orange-300 active:scale-[0.98] sm:h-9 sm:text-sm"
        >
          去淘宝
          <ArrowUpRight className="size-3.5 sm:size-4" aria-hidden="true" />
        </a>
      </CardContent>
    </Card>
  );
}

function HomeWechatBindBanner({ currentUser }: { currentUser: CurrentUser | null }) {
  const { data: bindStatus, isLoading } = useQuery({
    queryKey: WECHAT_BIND_STATUS_QUERY_KEY,
    queryFn: ({ signal }) => getWechatBindStatus({ signal }),
    enabled: !!currentUser,
  });

  const bindMutation = useMutation({
    mutationFn: () =>
      getWechatAuthUrl({
        provider: isWeChatBrowser() ? "official-account" : "open-platform",
      }),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "微信绑定初始化失败");
    },
  });

  if (!currentUser || isLoading || bindStatus?.bound) {
    return null;
  }

  return (
    <Card className="border-green-500/30 bg-linear-to-r from-green-500/10 via-emerald-500/5 to-transparent shadow-sm">
      <CardContent>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <p className="text-sm font-semibold text-green-700 dark:text-green-400">
              微信未绑定，建议尽快绑定
            </p>
            <p className="text-sm text-muted-foreground">
              绑定后可快速登录，提升账号安全性，后续找回账号也更方便。
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 rounded-full border border-green-500/20 bg-background/70 px-2 py-1 text-xs text-muted-foreground">
                <Zap className="size-3.5 text-green-600" />
                快速登录
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-green-500/20 bg-background/70 px-2 py-1 text-xs text-muted-foreground">
                <ShieldCheck className="size-3.5 text-green-600" />
                账号安全
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-green-500/20 bg-background/70 px-2 py-1 text-xs text-muted-foreground">
                <Link2 className="size-3.5 text-green-600" />
                便捷找回
              </span>
            </div>
          </div>

          <Button
            data-umami-event="home-quick-wechat-bind"
            size="lg"
            className="w-full sm:w-auto sm:shrink-0 bg-green-600 text-white shadow-md shadow-green-700/20 hover:bg-green-700"
            onClick={() => bindMutation.mutate()}
            disabled={bindMutation.isPending}
          >
            {bindMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Link2 className="size-4" />
            )}
            绑定微信
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function HomeNewsHighlightBlock() {
  const { data } = useQuery(latestNewsHighlightQueryOptions());
  const latest = data?.latest;
  const isTitleMissingHighlight = !latest?.title?.trim();
  const latestLabel = getNewsDisplayLabel({
    sourcePlatform: latest?.sourcePlatform,
    sourceAuthorHandle: latest?.sourceAuthorHandle,
    title: latest?.title,
    fallback: "最新新闻",
  });
  const coverImage = toNewsImageUrlForRender(latest?.coverImage, {
    referer: latest?.sourceUrl,
  });

  if (!latest) {
    return null;
  }

  return (
    <Card className="relative overflow-hidden p-0 border-red-500/30 bg-zinc-950 text-white shadow-lg shadow-red-900/10">
      {coverImage && (
        <img
          src={coverImage}
          alt={latestLabel}
          className="absolute inset-0 size-full object-cover"
          loading="lazy"
          decoding="async"
        />
      )}

      <div className="absolute inset-0 bg-linear-to-r from-black/85 via-black/60 to-black/30" />
      <div className="absolute inset-0 bg-linear-to-t from-red-900/35 via-transparent to-transparent" />

      <CardContent className="relative z-10 space-y-4 p-6">
        <div className="space-y-2">
          <p className="inline-flex items-center rounded-full border border-red-300/40 bg-red-500/20 px-2.5 py-1 text-xs font-semibold text-red-50">
            最新新闻
          </p>
          {isTitleMissingHighlight ? (
            <div className="max-w-2xl space-y-1 text-shadow-md">
              <p className="text-[11px] font-semibold tracking-[0.14em] text-cyan-100/90 uppercase">
                X Thread 摘要
              </p>
              <p className="line-clamp-4 whitespace-pre-line text-sm leading-relaxed text-zinc-100/92">
                {latest.summary || "查看该 X Thread 的最新动态与讨论内容。"}
              </p>
            </div>
          ) : (
            <>
              <h2 className="max-w-2xl line-clamp-2 text-2xl font-extrabold leading-tight md:text-3xl text-shadow-md">
                {latestLabel}
              </h2>
              <p className="max-w-xl line-clamp-3 whitespace-pre-line text-sm leading-relaxed text-zinc-100/90 md:text-base text-shadow-md">
                {latest.summary || "查看 Apex 官方最新动态与更新内容。"}
              </p>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-100/80">
          <span className="rounded-md border border-white/20 bg-black/30 px-2 py-1">
            {latest.sourceType}
          </span>
          {latest.sourceAuthorHandle ? (
            <span className="rounded-md border border-white/20 bg-black/30 px-2 py-1">
              @{latest.sourceAuthorHandle}
            </span>
          ) : null}
          <ClientOnly>
            <span className="rounded-md border border-white/20 bg-black/30 px-2 py-1">
              {formatNewsUtcDate(latest.publishingDate)}
            </span>
          </ClientOnly>
          {latest.featured && (
            <span className="rounded-md border border-red-200/40 bg-red-500/25 px-2 py-1">
              精选
            </span>
          )}
        </div>

        <div className="flex flex-wrap space-x-3">
          <Link to="/news/$slug" params={{ slug: latest.slug }}>
            <Button className="bg-red-600 text-white hover:bg-red-500">查看最新新闻详情</Button>
          </Link>
          <Link to="/news">
            <Button
              variant="outline"
              className="border-white/35 bg-black/20 text-white hover:bg-white/10 hover:text-white"
            >
              查看更多新闻
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
